import {z} from "zod/v4-mini"
import type {NodeParameters} from "@cm/graph-v3"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import {hasMarker} from "#template/role-markers"
import type {TemplateNodeDefinition} from "#template/template-node/types"
import type {GeometryAttributeSetData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryGraphNode} from "#template/nodes/geometry-graph/common/geometry-graph-node"

export abstract class GeometryAttributeSetNode<out T extends IndexingDim, out Parameters extends NodeParameters = NodeParameters> extends GeometryGraphNode<
    GeometryAttributeSetData<T>,
    Parameters
> {
    public abstract override get definition(): TemplateNodeDefinition<Parameters>

    protected override get evaluatedGeometryDataPrefix() {
        return "geometryAttributeSetData"
    }
}

////////////////////////////////////////////////////////////////////////////////
// Point dim (T = 0)

export const pointGeometryAttributeSetLikeMarker = Symbol("PointGeometryAttributeSetLike")

export interface PointGeometryAttributeSetLikeNode extends EvaluableTemplateNode<GeometryAttributeSetData<0> | null> {
    readonly [pointGeometryAttributeSetLikeMarker]: true
}

export const isPointGeometryAttributeSetLikeNode = (x: unknown): x is PointGeometryAttributeSetLikeNode => hasMarker(x, pointGeometryAttributeSetLikeMarker)

export const pointGeometryAttributeSetLike = z.custom<PointGeometryAttributeSetLikeNode>(isPointGeometryAttributeSetLikeNode)

////////////////////////////////////////////////////////////////////////////////
// Curve dim (T = 1)

export const curveGeometryAttributeSetLikeMarker = Symbol("CurveGeometryAttributeSetLike")

export interface CurveGeometryAttributeSetLikeNode extends EvaluableTemplateNode<GeometryAttributeSetData<1> | null> {
    readonly [curveGeometryAttributeSetLikeMarker]: true
}

export const isCurveGeometryAttributeSetLikeNode = (x: unknown): x is CurveGeometryAttributeSetLikeNode => hasMarker(x, curveGeometryAttributeSetLikeMarker)

export const curveGeometryAttributeSetLike = z.custom<CurveGeometryAttributeSetLikeNode>(isCurveGeometryAttributeSetLikeNode)

////////////////////////////////////////////////////////////////////////////////
// Mesh dim (T = 2)

export const meshGeometryAttributeSetLikeMarker = Symbol("MeshGeometryAttributeSetLike")

export interface MeshGeometryAttributeSetLikeNode extends EvaluableTemplateNode<GeometryAttributeSetData<2> | null> {
    readonly [meshGeometryAttributeSetLikeMarker]: true
}

export const isMeshGeometryAttributeSetLikeNode = (x: unknown): x is MeshGeometryAttributeSetLikeNode => hasMarker(x, meshGeometryAttributeSetLikeMarker)

export const meshGeometryAttributeSetLike = z.custom<MeshGeometryAttributeSetLikeNode>(isMeshGeometryAttributeSetLikeNode)
